import React, { useState, useEffect, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Fab,
  IconButton,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  alpha,
  CircularProgress
} from '@mui/material';
import {
  Add,
  Visibility,
  Edit,
  MoreVert,
  Search,
  FilterList,
  Download,
  Person,
  Phone,
  Email,
  EventNote,
  Male,
  Female,
  Transgender,
  CloudOff,
  CloudSync,
  TableChart,
  PictureAsPdf
} from '@mui/icons-material';
import { useFeedback } from './EnhancedFeedbackProvider';
import { useNotification } from '../contexts/NotificationContext';
import { useDataContext } from '../contexts/DataContext';
import useDataFetch from '../hooks/useDataFetch';
import PatientForm from './PatientForm';
import { generatePatientReport } from '../utils/pdfGenerator';
import { generatePatientExcelReport } from '../utils/excelExport';

const EnhancedPatientList = ({ showNewPatientForm = false }) => {
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [genderFilter, setGenderFilter] = useState('all');
  const [formOpen, setFormOpen] = useState(showNewPatientForm);
  const [sortBy, setSortBy] = useState('dateAdded');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const { showError, showSuccess } = useFeedback();
  const { showError: showNotificationError } = useNotification();
  
  // Utiliser le contexte de données pour les données
  const { 
    patients, 
    consultations, 
    vitals, 
    treatments, 
    medicalHistory, 
    loading, 
    addPatient, 
    updatePatient, 
    deletePatient, 
    loadAllData 
  } = useDataContext();

  // Handle showNewPatientForm prop changes
  useEffect(() => {
    setFormOpen(showNewPatientForm);
  }, [showNewPatientForm]);

  const filterAndSortPatients = useCallback(() => {
    if (!patients) return;
    console.log('Filtrage des patients:', patients.length, 'patients');
    let filtered = [...patients];

    // Filtrage par recherche
    if (searchTerm) {
      filtered = filtered.filter(patient => 
        `${patient.firstName} ${patient.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.phone?.includes(searchTerm) ||
        patient.medicalRecordNumber?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }


    // Filtrage par genre
    if (genderFilter !== 'all') {
      filtered = filtered.filter(patient => patient.gender === genderFilter);
    }

    // Tri
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
        case 'age':
          return new Date(b.dateOfBirth) - new Date(a.dateOfBirth);
        case 'dateAdded':
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        default:
          return 0;
      }
    });

    setFilteredPatients(filtered);
  }, [patients, searchTerm, genderFilter, sortBy]);

  useEffect(() => {
    if (patients) {
      console.log('Patients reçus dans EnhancedPatientList:', patients);
      filterAndSortPatients();
    }
  }, [patients, searchTerm, genderFilter, sortBy, filterAndSortPatients]);

  const handleMenuOpen = useCallback((event, patient) => {
    setAnchorEl(event.currentTarget);
    setSelectedPatient(patient);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setSelectedPatient(null);
  }, []);


  const getGenderIcon = (gender) => {
    switch (gender) {
      case 'M':
        return <Male color="primary" />;
      case 'F':
        return <Female color="secondary" />;
      default:
        return <Male color="action" />; // Par défaut, afficher l'icône masculin
    }
  };


  const calculateAge = (dateOfBirth) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  const formatDate = (date) => {
    if (!date) return 'Jamais';
    return new Date(date).toLocaleDateString('fr-FR');
  };

  const handleExportPatients = async () => {
    try {
      // Utiliser les données locales
      const allPatients = patients;

      // Créer le contenu CSV
      const csvContent = [
        // En-têtes
        ['Nom', 'Prénom', 'Date de naissance', 'Âge', 'Sexe', 'Email', 'Téléphone', 'Adresse', 'Contact d\'urgence', 'Téléphone d\'urgence', 'Numéro de dossier', 'Médecin', 'Date de création'].join(','),
        // Données
        ...allPatients.map(patient => [
          `"${patient.lastName || ''}"`,
          `"${patient.firstName || ''}"`,
          `"${formatDate(patient.dateOfBirth)}"`,
          `"${calculateAge(patient.dateOfBirth)}"`,
          `"${patient.gender || ''}"`,
          `"${patient.email || ''}"`,
          `"${patient.phone || ''}"`,
          `"${patient.address || ''}"`,
          `"${patient.emergencyContact || ''}"`,
          `"${patient.emergencyPhone || ''}"`,
          `"${patient.medicalRecordNumber || ''}"`,
          `"${patient.createdBy || 'Non spécifié'}"`,
          `"${formatDate(patient.createdAt)}"`
        ].join(','))
      ].join('\n');

      // Créer et télécharger le fichier
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `patients_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      showSuccess('Liste des patients exportée avec succès');
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      showError('Erreur lors de l\'export de la liste des patients');
    }
  };

  const handleExport = async (format, patient) => {
    if (!patient) return;
    
    try {
      // Récupérer les données du patient
      const patientConsultations = consultations.filter(c => c.patientId === patient.id);
      const patientVitals = vitals.filter(v => v.patientId === patient.id);
      const patientTreatments = treatments.filter(t => t.patientId === patient.id);
      const patientMedicalHistory = medicalHistory.filter(h => h.patientId === patient.id);

      // Générer l'export selon le format
      if (format === 'pdf') {
        generatePatientReport(
          patient,
          patientConsultations,
          patientVitals,
          patientTreatments,
          patientMedicalHistory
        );
        showSuccess(`Dossier PDF généré pour ${patient.firstName} ${patient.lastName}`);
      } else if (format === 'excel') {
        generatePatientExcelReport(
          patient,
          patientConsultations,
          patientVitals,
          patientTreatments,
          patientMedicalHistory
        );
        showSuccess(`Dossier Excel généré pour ${patient.firstName} ${patient.lastName}`);
      }
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      showError('Erreur lors de l\'export du dossier');
    }
  };


  return (
    <Box sx={{ p: 3 }}>
      {/* En-tête avec actions */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Liste des Patients
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExportPatients}
            sx={{ borderRadius: 2 }}
          >
            Exporter
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/patients/new')}
            sx={{ borderRadius: 2 }}
          >
            Nouveau Patient
          </Button>
        </Box>
      </Box>

      {/* Filtres et recherche */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Rechercher par nom, téléphone, dossier..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ borderRadius: 2 }}
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Genre</InputLabel>
                <Select
                  value={genderFilter}
                  onChange={(e) => setGenderFilter(e.target.value)}
                  label="Genre"
                >
                  <MenuItem value="all">Tous</MenuItem>
                  <MenuItem value="M">Masculin</MenuItem>
                  <MenuItem value="F">Féminin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Trier par</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  label="Trier par"
                >
                  <MenuItem value="name">Nom</MenuItem>
                  <MenuItem value="age">Âge</MenuItem>
                  <MenuItem value="dateAdded">Date d'ajout</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Box display="flex" gap={1}>
                <Chip
                  label={`${filteredPatients.length} patients`}
                  color="primary"
                  variant="outlined"
                />
                {filteredPatients.some(p => p.isLocal) && (
                  <Chip
                    icon={<CloudOff />}
                    label="Hors ligne"
                    color="warning"
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Indicateur de chargement */}
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary" ml={2}>
            Chargement des patients...
          </Typography>
        </Box>
      )}

      {/* Tableau des patients */}
      {!loading && (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 600 }}>Patient</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Âge</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Genre</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Contact</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Médecin</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPatients
              .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
              .map((patient) => (
              <TableRow 
                key={patient.id}
                hover
                sx={{ 
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  },
                  cursor: 'pointer'
                }}
                onClick={() => navigate(`/patients/${patient.id}`)}
              >
                <TableCell>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar
                      sx={{
                        bgcolor: 'primary.main',
                        width: 40,
                        height: 40,
                      }}
                    >
                      <Person />
                    </Avatar>
                    <Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {patient.firstName} {patient.lastName}
                        </Typography>
                        {patient.isLocal && (
                          <Tooltip title="Patient créé hors ligne">
                            <CloudOff fontSize="small" color="warning" />
                          </Tooltip>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Dossier: {patient.medicalRecordNumber || 'N/A'}
                      </Typography>
                      {patient.createdBy && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Créé par: {patient.createdBy}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </TableCell>
                
                <TableCell>
                  <Typography variant="body2">
                    {calculateAge(patient.dateOfBirth)} ans
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(patient.dateOfBirth)}
                  </Typography>
                </TableCell>
                
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    {getGenderIcon(patient.gender)}
                    <Typography variant="body2">
                      {patient.gender === 'M' ? 'Masculin' : 
                       patient.gender === 'F' ? 'Féminin' : 'Masculin'}
                    </Typography>
                  </Box>
                </TableCell>
                
                <TableCell>
                  <Box>
                    {patient.phone && (
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <Phone fontSize="small" color="action" />
                        <Typography variant="body2">{patient.phone}</Typography>
                      </Box>
                    )}
                    {patient.email && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Email fontSize="small" color="action" />
                        <Typography variant="body2">{patient.email}</Typography>
                      </Box>
                    )}
                  </Box>
                </TableCell>
                
                <TableCell>
                  {patient.createdBy ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Person fontSize="small" color="primary" />
                      <Typography 
                        variant="body2" 
                        color="primary"
                      >
                        {patient.createdBy}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Non spécifié
                    </Typography>
                  )}
                </TableCell>
                
                <TableCell align="center">
                  <Box display="flex" justifyContent="center" gap={1}>
                    <Tooltip title="Voir le dossier">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/patients/${patient.id}`);
                        }}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="Modifier">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/patients/${patient.id}?edit=true`);
                        }}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="Exporter le dossier">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuOpen(e, patient);
                        }}
                      >
                        <Download />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {/* Menu d'export */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => {
          handleExport('excel', selectedPatient);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <TableChart fontSize="small" />
          </ListItemIcon>
          <ListItemText>Exporter en Excel</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => {
          handleExport('pdf', selectedPatient);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <PictureAsPdf fontSize="small" />
          </ListItemIcon>
          <ListItemText>Exporter en PDF</ListItemText>
        </MenuItem>
      </Menu>

      {/* FAB pour nouveau patient */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{ 
          position: 'fixed', 
          bottom: 24, 
          right: 24,
          boxShadow: 3,
        }}
        onClick={() => navigate('/patients/new')}
      >
        <Add />
      </Fab>

      {/* Patient Form */}
      <PatientForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          navigate('/patients');
        }}
        onSave={(patientData) => {
          // Handle patient save
          setFormOpen(false);
          navigate('/patients');
        }}
      />
    </Box>
  );
};

export default memo(EnhancedPatientList);
